import React, { useCallback, useEffect, useState } from 'react';
import { confirmAction } from '../../../utils/confirm';
import {
    fetchAlertRules, fetchAlertContacts, createAlertContact, updateAlertContact, deleteAlertContact,
    setContactRules,
} from '../../../services/api';
import { fetchAdvancedRules, AdvancedRule } from '../../../services/advancedRulesApi';
import { AlertRule, AlertContact } from '../../../types';
import { blankContact } from '../surveillanceShared';

/**
 * Kontaktlistan och kontaktformuläret: hämtar kontakter, regler och avancerade
 * regler, och har hanterare för ny, ändra, spara, avbryt, ta bort och tilldela regler.
 */
export function useContactForm() {
    const [contacts, setContacts] = useState<AlertContact[]>([]);
    const [rules, setRules] = useState<AlertRule[]>([]);
    const [advancedRules, setAdvancedRules] = useState<AdvancedRule[]>([]);
    const [error, setError] = useState<string | null>(null);

    const [showForm, setShowForm] = useState(false);
    const [editContactId, setEditContactId] = useState<number | null>(null);
    const [form, setForm] = useState(blankContact());

    const load = useCallback(async () => {
        const [resContacts, resRules, resAdvanced] = await Promise.allSettled([
            fetchAlertContacts(), fetchAlertRules(), fetchAdvancedRules(),
        ]);
        if (resContacts.status === 'fulfilled') setContacts(resContacts.value);
        if (resRules.status === 'fulfilled') setRules(resRules.value);
        if (resAdvanced.status === 'fulfilled') setAdvancedRules(resAdvanced.value);
        const failed = [resContacts, resRules, resAdvanced].find(r => r.status === 'rejected') as PromiseRejectedResult | undefined;
        setError(failed ? (failed.reason?.message || 'Kunde inte hämta kontakterna') : null);
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setEditContactId(null); setForm(blankContact()); setShowForm(true); };

    const openEdit = (c: AlertContact) => {
        setEditContactId(c.id);
        setForm({
            name: c.name, email: c.email || '', phone: c.phone || '',
            notify_email: c.notify_email, notify_sms: c.notify_sms, notify_push: c.notify_push,
            alert_hours_start: (c.alert_hours_start || '').slice(0, 5),
            alert_hours_end:   (c.alert_hours_end   || '').slice(0, 5),
            alert_days_mask:   c.alert_days_mask ?? 127,
            timezone:          c.timezone || 'Europe/Stockholm',
            defer_offhours:    c.defer_offhours ?? false,
        });
        setShowForm(true);
    };

    const cancel = () => setShowForm(false);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editContactId !== null) await updateAlertContact(editContactId, form);
            else await createAlertContact(form);
            setShowForm(false);
            await load();
        } catch (err: any) { setError(err.message); }
    };

    const remove = async (id: number) => {
        if (!confirmAction('Ta bort den här kontakten? Scheman och mallar som pekar på den tappar sin mottagare.')) return;
        try { await deleteAlertContact(id); await load(); } catch (err: any) { setError(err.message); }
    };

    /** Sätter exakt vilka regler kontakten får – avbockade tas bort. */
    const setRulesFor = async (contactId: number, ruleIds: number[], advancedRuleIds: number[]) => {
        try { await setContactRules(contactId, ruleIds, advancedRuleIds); await load(); }
        catch (err: any) { setError(err.message); }
    };

    return {
        contacts, rules, advancedRules, error, setError,
        showForm, editContactId, form, setForm,
        openCreate, openEdit, cancel, submit, remove, setRulesFor,
    };
}
